import React, { useMemo } from 'react';
import { Box, Field, FieldTypes, Inscription, RegularField } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import Permissions from '@/common/permissions';
import routes from '@/common/routes';
import { DefaultFields } from '@/common/types';
import OrganizationLockIcon from '@/components/elements/OrganizationLockIcon/OrganizationLockIcon';
import { QueryKeys, useHasAccess } from '@/modules/core/hooks';
import { UNUSED_EMPLOYMENT_STATUSES } from '@/modules/employee/employee.constants';
import { SourceIds } from '@/modules/employee/employee.types';
// import { REGEXPS } from '@/common';
// import { useSSNMask } from '@/modules/core/hooks';
// import { SSN_INVALID_TEXT } from '@/common/constants';
import { EmployerStatuses } from '@/modules/employee/Employee/Employee.types';
import { getIdsFromPath } from '@/utils';

import { ControlWithLock, LockAside, LockBesideControl } from '../EmployerInfo.styles';
import { Subsidiary } from '../queries/useSubsidiaries.query';
import useEmployerInfoStore, { EmployerInfoPayload } from '../stores/useEmployerInfo.store';

export const goToEmployerPage = (organizationPath?: string) => {
  const { partnerId, distributorId, employerId } = getIdsFromPath(organizationPath);
  const searchParams = new URLSearchParams({
    [QueryKeys.OBSERVING_PARTNER_QUERY_KEY]: partnerId,
    [QueryKeys.OBSERVING_DISTRIBUTOR_QUERY_KEY]: distributorId,
    [QueryKeys.OBSERVING_EMPLOYER_QUERY_KEY]: employerId,
  });
  window.open(`${routes.EMPLOYERS}/${employerId}?${searchParams.toString()}`);
};

const REQUIRED_PERMISSIONS = [{ permission: Permissions.VIEW_ER }, { permission: Permissions.VIEW_PROFILE }];

interface Props {
  isEditMode?: boolean;
  subsidiaries?: Subsidiary[];
  defaultFields?: Partial<EmployerInfoPayload>;
  isSubsidiary?: boolean;
  ssnField?: Field;
  payrollGroupField?: Field;
  hasAccessToEditPrimaryId?: boolean;
  notes?: string;
  isOrganizationLocked?: boolean;
}

const useEmployerInfoFields = ({
  isEditMode,
  subsidiaries,
  defaultFields,
  isSubsidiary,
  ssnField,
  payrollGroupField,
  hasAccessToEditPrimaryId,
  notes,
  isOrganizationLocked,
}: Props) => {
  // const ssn = useEmployerInfoStore((state) => state.state.ssn);
  // const currentSSNMask = useSSNMask(ssn);
  const {
    sourceState,
    state: { employerStatus, subsidiary: selectedSubsidiaryId },
  } = useEmployerInfoStore((state) => state);
  const isStatusChanged = useMemo(
    () => sourceState?.employerStatus !== employerStatus,
    [sourceState, employerStatus]
  );
  const hasAccess = useHasAccess(REQUIRED_PERMISSIONS);
  const hasActiveSubsidiaries = useMemo(() => !!subsidiaries?.length, [subsidiaries]);

  const isParentOrganizationSubsidiary = useMemo(
    () => !!subsidiaries?.find((subsidiary) => subsidiary.id === defaultFields?.organizationId),
    [subsidiaries, defaultFields]
  );
  const isSelectedSubsidiaryLocked = useMemo(
    () => !!subsidiaries?.find((subsidiary) => subsidiary.id === (selectedSubsidiaryId || defaultFields?.organizationId))
      ?.isLocked,
    [subsidiaries, selectedSubsidiaryId, defaultFields?.organizationId]
  );
  const subsidiaryName = useMemo(
    () => subsidiaries?.find((subsidiary) => subsidiary.id === defaultFields?.organizationId)?.name,
    [subsidiaries, defaultFields?.organizationId]
  );
  const sourceId = useEmployerInfoStore((state) => state.state.sourceId);

  const isEEIDRequired = useMemo(() => sourceId === SourceIds.EEID, [sourceId]);
  const isPEIDRequired = useMemo(() => sourceId === SourceIds.PEID, [sourceId]);

  const fields = useMemo(
    () => [
      {
        name: 'employerName',
        ...(isEditMode
          ? isOrganizationLocked
            ? {
                type: FieldTypes.Node,
                node: (
                  <ControlWithLock>
                    <RegularField
                      name="employerName"
                      type={FieldTypes.Text}
                      editMode
                      disabled
                      isThinMode
                      isDisabledMode
                      value={defaultFields?.employerName || ''}
                      onChange={() => undefined}
                    />
                    <LockAside $hasNotes={hasAccess && !!notes}>
                      <OrganizationLockIcon testId="employer-info_employer-lock" />
                    </LockAside>
                  </ControlWithLock>
                ),
              }
            : { type: FieldTypes.Text }
          : {
              type: FieldTypes.Node,
              node: (
                <Box direction="row" align="center">
                  <Inscription
                    color="textAccent"
                    cursor="pointer"
                    lineHeight="22px"
                    size="large"
                    onClick={() => goToEmployerPage(defaultFields?.organizationPath)}
                  >
                    {defaultFields?.employerName}
                  </Inscription>
                  {isOrganizationLocked && (
                    <Box margin={{ left: 'spacing12' }}>
                      <OrganizationLockIcon testId="employer-info_employer-lock" />
                    </Box>
                  )}
                </Box>
              ),
            }),
        label: 'Employer name',
        disabled: true,
        notes: hasAccess ? notes : undefined,
        notesTitle: 'Employer notes',
        value: defaultFields?.employerName,
      },
      {
        name: 'partnerEmployeeId',
        type: FieldTypes.Text,
        label: 'Partner Employee ID',
        showRequireIcon: isPEIDRequired,
        validator: isPEIDRequired ? VALIDATORS.REQUIRED_STRING : VALIDATORS.STRING,
        disabled: !hasAccessToEditPrimaryId || !isEditMode,
        value: defaultFields?.partnerEmployeeId,
      },
      {
        name: 'employerEmployeeId',
        type: FieldTypes.Text,
        label: 'Employer Employee ID',
        showRequireIcon: isEEIDRequired,
        validator: isEEIDRequired ? VALIDATORS.REQUIRED_STRING : VALIDATORS.STRING,
        disabled: !hasAccessToEditPrimaryId || !isEditMode,
        value: defaultFields?.employerEmployeeId,
      },
      {
        name: 'internalId',
        type: FieldTypes.Text,
        label: 'System ID',
        disabled: true,
        placeholder: 'Enter Internal ID',
        value: defaultFields?.uniqueId,
      },
      ...(ssnField ? [ssnField] : []),
      ...(payrollGroupField ? [payrollGroupField] : []),
      // ...defaultFields?.payrollGroupId ? [{
      //   name: 'payrollGroupCode',
      //   type: FieldTypes.Text,
      //   label: 'Payroll Group Code',
      //   disabled: true,
      //   value: defaultFields?.payrollGroupId,
      // }] : [],
      ...(defaultFields?.validFrom
        ? [
            {
              name: 'payrollGroupEffectiveDate',
              type: FieldTypes.Text,
              label: 'Payroll Group Effective Date',
              disabled: true,
              value: dayjs(defaultFields?.validFrom).format(DEFAULT_DATE_FORMAT),
            },
          ]
        : []),
      isParentOrganizationSubsidiary
        ? {
            name: 'subsidiary',
            ...(!isEditMode && isOrganizationLocked
              ? {
                  type: FieldTypes.Node,
                  node: (
                    <Box direction="row" align="center">
                      <Inscription lineHeight="20px">{subsidiaryName}</Inscription>
                      <Box margin={{ left: 'spacing12' }}>
                        <OrganizationLockIcon testId="employer-info_subsidiary-lock" />
                      </Box>
                    </Box>
                  ),
                }
              : {
                  type: FieldTypes.Dropdown,
                  ...(isEditMode && isSelectedSubsidiaryLocked
                    ? {
                        fieldControlStyle: { position: 'relative' as const },
                        childrenNode: (
                          <LockBesideControl>
                            <OrganizationLockIcon testId="employer-info_subsidiary-lock" />
                          </LockBesideControl>
                        ),
                      }
                    : {}),
                }),
            label: 'Subsidiary',
            singleMode: true,
            disabled: !isEditMode || !hasActiveSubsidiaries || isSubsidiary,
            value: defaultFields?.organizationId,
            placeholder: !hasActiveSubsidiaries ? 'No active subsidiaries' : 'Select subsidiary',
            options: subsidiaries?.map((subsidiary) => ({
              key: subsidiary.id,
              value: subsidiary.name,
            })),
            validator: yup.string().trim(),
          }
        : {
            name: 'parentOrganizationId',
            type: FieldTypes.Dropdown,
            label: 'Subsidiary',
            singleMode: true,
            disabled: !isEditMode || !hasActiveSubsidiaries || isSubsidiary,
            placeholder: !hasActiveSubsidiaries ? 'No active subsidiaries' : 'Select subsidiary',
            options: subsidiaries?.map((subsidiary) => ({
              key: subsidiary.id,
              value: subsidiary.name,
            })),
            validator: yup.string().trim(),
          },
      {
        name: 'employerStatus',
        type: FieldTypes.Dropdown,
        label: 'Status',
        singleMode: true,
        disabled: !isEditMode,
        placeholder: 'Select status',
        options: EmployerStatuses,
        validator: yup.string().trim(),
      },
      {
        name: 'statusEffectiveDate',
        type: FieldTypes.Date,
        isManualDateInput: true,
        label: 'Status effective date',
        disabled: !isEditMode || !isStatusChanged,
        placeholder: 'Enter status effective date',
        validator: yup.string().trim(),
      },
      // {
      //   name: 'payrollGroup',
      //   type: FieldTypes.Dropdown,
      //   label: 'Payroll group',
      //   disabled,
      // singleMode: true,
      //   placeholder: 'Select payroll group',
      //   options: [{
      //     key: 'weekly',
      //     value: 'Weekly',
      //   }, {
      //     key: 'monthly',
      //     value: 'monthly',
      //   }],
      //   validator: yup.string().trim(),
      // },
      // {
      //   name: 'hireDate',
      //   type: FieldTypes.Date,
      //   isManualDateInput: true,
      //   label: 'Hire date',
      //   disabled: !isEditMode,
      //   placeholder: 'Enter hire date',
      //   validator: yup.string().trim(),
      // },
      // {
      //   name: 'terminationDate',
      //   type: FieldTypes.Date,
      //   isManualDateInput: true,
      //   label: 'Termination date',
      //   disabled: !isEditMode,
      //   placeholder: 'Enter termination date',
      //   validator: yup.string().trim(),
      // },
    ],
    [
      isPEIDRequired,
      isEEIDRequired,
      ssnField,
      defaultFields,
      isEditMode,
      subsidiaries,
      isStatusChanged,
      hasActiveSubsidiaries,
      isSubsidiary,
      isParentOrganizationSubsidiary,
      hasAccessToEditPrimaryId,
      payrollGroupField,
      notes,
      hasAccess,
      isOrganizationLocked,
      isSelectedSubsidiaryLocked,
      subsidiaryName,
    ]
  );
  const isStatusHidden = UNUSED_EMPLOYMENT_STATUSES.includes(`${defaultFields?.employerStatus}`);
  const formatedFields = useMemo(() => {
    const displayedFields = isStatusHidden ? fields.filter((item) => item.name !== 'employerStatus') : fields;
    if (defaultFields) {
      const defaultValues = defaultFields as DefaultFields;
      return displayedFields.map((item) => ({
        ...item,
        value: item?.value || defaultValues[item?.name || ''] || '',
      })) as Field[];
    }
    return displayedFields as Field[];
  }, [defaultFields, fields, isStatusHidden]);

  return formatedFields;
};

export default useEmployerInfoFields;
